import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { fetchApiData } from "@/lib/apiHelpers";
import { fetchIdentityApiData } from "@/lib/identityApiHelpers";

// CMS API Types based on updated swagger.json
interface SendOtpRequest {
  email: string;
}

interface SendOtpResponse {
  success: boolean;
  message: string;
  expiresAt: string;
}

interface VerifyOtpRequest {
  email: string;
  code: string;
}

import type { PhotoResponse } from '@/types/photos';

// Updated to match backend UserInfoResponse schema
interface CmsUser {
  id: string; // UUID
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  photo?: PhotoResponse;
  roles: string[];
  accessTypes: string[]; // New field from backend
  organizationId?: string;
  employeeId?: string;
}

interface VerifyOtpResponse {
  user: CmsUser;
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  authMethod: string;
}

interface UpdateProfileRequest {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
}

// Microsoft SSO Types
interface EntraConfigResponse {
  organizationId: string;
  subdomain?: string;
  organizationName?: string;
  isActive: boolean;
  clientId?: string;
  tenantId?: string;
  authority?: string;
  scopes?: string[];
}

interface TokenExchangeRequest {
  entraAccessToken: string;
}

interface TokenExchangeResponse {
  accessToken: string;
  expiresAt: string;
  user: CmsUser;
}

// Secure token storage
const TOKEN_KEY = 'athlyn_cms_token';
const REFRESH_TOKEN_KEY = 'athlyn_cms_refresh';
const USER_KEY = 'athlyn_cms_user';
const AUTH_METHOD_KEY = 'athlyn_cms_auth_method';
const MICROSOFT_TOKENS_KEY = 'athlyn_cms_microsoft_tokens';

interface MicrosoftTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  scope: string;
}

class AuthStorage {
  static setTokens(accessToken: string, refreshToken: string, expiresAt: string, user: CmsUser, authMethod: 'otp' | 'sso' = 'otp', microsoftTokens?: MicrosoftTokens) {
    // Store in sessionStorage for security (cleared when browser closes)
    sessionStorage.setItem(TOKEN_KEY, accessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    sessionStorage.setItem(USER_KEY, JSON.stringify({ user, expiresAt }));
    sessionStorage.setItem(AUTH_METHOD_KEY, authMethod);
    
    if (microsoftTokens) {
      sessionStorage.setItem(MICROSOFT_TOKENS_KEY, JSON.stringify(microsoftTokens));
    }
  }

  static getAccessToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  static getStoredUser(): { user: CmsUser; expiresAt: string } | null {
    const stored = sessionStorage.getItem(USER_KEY);
    if (!stored) return null;
    
    try {
      const parsed = JSON.parse(stored);
      // Check if token is expired
      if (new Date(parsed.expiresAt) <= new Date()) {
        this.clearAll();
        return null;
      }
      return parsed;
    } catch {
      this.clearAll();
      return null;
    }
  }

  static getAuthMethod(): 'otp' | 'sso' | null {
    return sessionStorage.getItem(AUTH_METHOD_KEY) as 'otp' | 'sso' | null;
  }

  static getMicrosoftTokens(): MicrosoftTokens | null {
    const stored = sessionStorage.getItem(MICROSOFT_TOKENS_KEY);
    if (!stored) return null;
    
    try {
      const tokens = JSON.parse(stored);
      // Check if Microsoft tokens are expired
      if (new Date(tokens.expiresAt) <= new Date()) {
        this.clearMicrosoftTokens();
        return null;
      }
      return tokens;
    } catch {
      this.clearMicrosoftTokens();
      return null;
    }
  }

  static clearMicrosoftTokens() {
    sessionStorage.removeItem(MICROSOFT_TOKENS_KEY);
  }

  static clearAll() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(AUTH_METHOD_KEY);
    sessionStorage.removeItem(MICROSOFT_TOKENS_KEY);
  }

  static isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }

  static isTokenExpiringSoon(minutesThreshold: number = 5): boolean {
    const stored = this.getStoredUser();
    if (!stored) return false;
    
    const expirationTime = new Date(stored.expiresAt);
    const now = new Date();
    const timeDifference = expirationTime.getTime() - now.getTime();
    const minutesUntilExpiration = timeDifference / (1000 * 60);
    
    return minutesUntilExpiration <= minutesThreshold;
  }
}

export function useAuth() {
  // Check authentication status and handle automatic token refresh
  const {
    data: authState,
    isLoading
  } = useQuery({
    queryKey: ["auth-state"],
    queryFn: async () => {
      const stored = AuthStorage.getStoredUser();
      const isAuthenticated = AuthStorage.isAuthenticated();
      
      // Check if token is expiring soon and needs refresh (only for SSO)
      if (isAuthenticated && AuthStorage.isTokenExpiringSoon(5)) {
        const authMethod = AuthStorage.getAuthMethod();
        
        if (authMethod === 'sso') {
          try {
            // Automatically refresh Microsoft SSO token
            const microsoftTokens = AuthStorage.getMicrosoftTokens();
            if (microsoftTokens) {
              await refreshMicrosoftTokenSilently();
            }
          } catch (error) {
            console.warn('Failed to refresh SSO token silently:', error);
            // Don't throw error here - let the user continue and handle auth errors in API calls
          }
        }
        // For OTP method, identity API handles refresh automatically
      }
      
      return {
        user: stored?.user || null,
        isAuthenticated: AuthStorage.isAuthenticated(), // Re-check after potential refresh
        isLoading: false
      };
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Check every minute for auto-refresh
  });

  // Silent Microsoft token refresh function
  const refreshMicrosoftTokenSilently = async () => {
    const microsoftTokens = AuthStorage.getMicrosoftTokens();
    const entraConfigData = getEntraConfig.data;
    
    if (!microsoftTokens || !entraConfigData) {
      throw new Error('No Microsoft tokens or configuration available');
    }

    // Refresh Microsoft token using their API
    const tokenResponse = await fetch(`${entraConfigData.authority}/oauth2/v2.0/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: entraConfigData.clientId || '',
        scope: microsoftTokens.scope || entraConfigData.scopes?.join(' ') || 'openid profile email',
        refresh_token: microsoftTokens.refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    if (!tokenResponse.ok) {
      throw new Error('Failed to refresh Microsoft token');
    }

    const tokenData = await tokenResponse.json();
    
    const newMicrosoftTokens: MicrosoftTokens = {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token || microsoftTokens.refreshToken,
      expiresAt: new Date(Date.now() + tokenData.expires_in * 1000).toISOString(),
      scope: microsoftTokens.scope
    };

    // Exchange refreshed Microsoft token for new Athlyn token
    const athlynTokens = await fetchIdentityApiData<TokenExchangeResponse>("POST", "/auth/token/exchange", {
      entraAccessToken: newMicrosoftTokens.accessToken
    });

    // Update stored tokens
    const stored = AuthStorage.getStoredUser();
    if (stored && athlynTokens.user && athlynTokens.accessToken) {
      AuthStorage.setTokens(
        athlynTokens.accessToken,
        '',
        athlynTokens.expiresAt,
        athlynTokens.user,
        'sso',
        newMicrosoftTokens
      );
    }

    return newMicrosoftTokens;
  };

  // Request Email OTP
  const requestOtp = useMutation({
    mutationFn: async (request: SendOtpRequest): Promise<SendOtpResponse> => {
      const data = await fetchIdentityApiData<SendOtpResponse>("POST", "/auth/otp/request", request);
      
      if (!data.success) {
        throw new Error('Failed to send OTP');
      }
      
      return data;
    },
  });

  // Verify Email OTP and login
  const verifyOtp = useMutation({
    mutationFn: async (request: VerifyOtpRequest): Promise<VerifyOtpResponse> => {
      const data = await fetchIdentityApiData<VerifyOtpResponse>("POST", "/auth/otp/verify", request);
      
      if (!data.user || !data.accessToken) {
        throw new Error('OTP verification failed');
      }
      
      // Store tokens securely
      AuthStorage.setTokens(
        data.accessToken,
        data.refreshToken, 
        data.expiresAt,
        data.user,
        'otp'
      );
      
      return data;
    },
  });

  // Get Entra/SSO configuration
  const getEntraConfig = useQuery({
    queryKey: ["entra-config"],
    queryFn: () => fetchIdentityApiData<EntraConfigResponse>("GET", "/auth/config"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry if SSO is not configured
  });

  // Exchange Entra token for Athlyn token
  const exchangeEntraToken = useMutation({
    mutationFn: async (request: TokenExchangeRequest & { microsoftTokens?: MicrosoftTokens }): Promise<TokenExchangeResponse> => {
      const data = await fetchIdentityApiData<TokenExchangeResponse>("POST", "/auth/token/exchange", {
        entraAccessToken: request.entraAccessToken
      });
      
      if (!data.user || !data.accessToken) {
        throw new Error('Token exchange failed');
      }
      
      // Store tokens securely with Microsoft tokens for refresh
      AuthStorage.setTokens(
        data.accessToken,
        '', // No Athlyn refresh token from SSO flow
        data.expiresAt,
        data.user,
        'sso',
        request.microsoftTokens
      );
      
      return data;
    },
  });

  // Get current user info from API
  const getCurrentUser = useQuery({
    queryKey: ["/api/v1/auth/me"],
    queryFn: () => fetchApiData<CmsUser>("GET", "/api/v1/auth/me"),
    enabled: AuthStorage.isAuthenticated() && !AuthStorage.getStoredUser()?.user,
    retry: (failureCount, error: any) => {
      // Don't retry on 401/403 (auth errors)
      if (error?.status === 401 || error?.status === 403) {
        AuthStorage.clearAll();
        queryClient.invalidateQueries({ queryKey: ["auth-state"] });
        return false;
      }
      return failureCount < 2;
    },
  });

  // Update profile
  const updateProfile = useMutation({
    mutationFn: async (request: UpdateProfileRequest): Promise<CmsUser> => {
      const response = await fetchApiData<CmsUser>("PUT", "/api/v1/auth/profile", request);
      
      // Update stored user
      const stored = AuthStorage.getStoredUser();
      if (stored) {
        AuthStorage.setTokens(
          AuthStorage.getAccessToken()!,
          AuthStorage.getRefreshToken()!,
          stored.expiresAt,
          response // Updated user info
        );
      }
      
      queryClient.invalidateQueries({ queryKey: ["auth-state"] });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/auth/me"] });
      
      return response;
    },
    onError: (error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        AuthStorage.clearAll();
        queryClient.invalidateQueries({ queryKey: ["auth-state"] });
      }
    },
  });

  // Logout
  const logout = useMutation({
    mutationFn: async () => {
      try {
        await fetchApiData("POST", "/api/v1/auth/logout");
      } catch (error) {
        // Continue with logout even if API call fails
        console.warn('Logout API call failed:', error);
      } finally {
        // Always clear local storage
        AuthStorage.clearAll();
        queryClient.clear();
        // Force a re-evaluation of auth state
        queryClient.invalidateQueries({ queryKey: ["auth-state"] });
        // Force page refresh to trigger ProtectedRoute re-evaluation
        window.location.reload();
      }
    },
  });

  // Refresh Microsoft tokens
  const refreshMicrosoftToken = useMutation({
    mutationFn: async (): Promise<MicrosoftTokens> => {
      const microsoftTokens = AuthStorage.getMicrosoftTokens();
      const entraConfig = getEntraConfig.data;
      
      if (!microsoftTokens || !entraConfig) {
        throw new Error('No Microsoft tokens or configuration available');
      }

      // Refresh Microsoft token using their API
      const tokenResponse = await fetch(`${entraConfig.authority}/oauth2/v2.0/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: entraConfig.clientId || '',
          scope: microsoftTokens.scope || entraConfig.scopes?.join(' ') || 'openid profile email',
          refresh_token: microsoftTokens.refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to refresh Microsoft token');
      }

      const tokenData = await tokenResponse.json();
      
      const newMicrosoftTokens: MicrosoftTokens = {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token || microsoftTokens.refreshToken,
        expiresAt: new Date(Date.now() + tokenData.expires_in * 1000).toISOString(),
        scope: microsoftTokens.scope
      };

      // Exchange refreshed Microsoft token for new Athlyn token
      const athlynTokens = await fetchIdentityApiData<TokenExchangeResponse>("POST", "/auth/token/exchange", {
        entraAccessToken: newMicrosoftTokens.accessToken
      });

      // Update stored tokens
      const stored = AuthStorage.getStoredUser();
      if (stored && athlynTokens.user && athlynTokens.accessToken) {
        AuthStorage.setTokens(
          athlynTokens.accessToken,
          '',
          athlynTokens.expiresAt,
          athlynTokens.user,
          'sso',
          newMicrosoftTokens
        );
      }

      return newMicrosoftTokens;
    },
    onError: () => {
      // If refresh fails, clear everything and force re-login
      AuthStorage.clearAll();
      queryClient.invalidateQueries({ queryKey: ["auth-state"] });
    },
  });

  // Refresh token (only handles SSO method - OTP refresh is handled by identity API)
  const refreshToken = useMutation({
    mutationFn: async () => {
      const authMethod = AuthStorage.getAuthMethod();
      
      if (authMethod === 'sso') {
        // For SSO, refresh Microsoft tokens and exchange
        return await refreshMicrosoftToken.mutateAsync();
      } else {
        // For OTP, we don't handle refresh locally - identity API handles this
        throw new Error('Token refresh for OTP method should be handled by identity API automatically');
      }
    },
    onError: () => {
      // If refresh fails, clear everything and force re-login
      AuthStorage.clearAll();
      queryClient.invalidateQueries({ queryKey: ["auth-state"] });
    },
  });

  return {
    // State
    user: authState?.user || null,
    isAuthenticated: authState?.isAuthenticated || false,
    isLoading,
    
    // Actions
    requestOtp,
    verifyOtp,
    exchangeEntraToken,
    logout,
    updateProfile,
    refreshToken,
    
    // Queries
    getCurrentUser,
    getEntraConfig,
    
    // Loading states
    isRequestingOtp: requestOtp.isPending,
    isVerifyingOtp: verifyOtp.isPending,
    isExchangingEntraToken: exchangeEntraToken.isPending,
    isLoggingOut: logout.isPending,
    isUpdatingProfile: updateProfile.isPending,
    
    // SSO State
    entraConfig: getEntraConfig.data,
    isLoadingEntraConfig: getEntraConfig.isLoading,
    isSsoEnabled: getEntraConfig.data?.isActive || false,
    
    // Storage utilities
    clearAuth: () => {
      AuthStorage.clearAll();
      queryClient.invalidateQueries({ queryKey: ["auth-state"] });
    },
  };
}

export default useAuth;
export { AuthStorage };